import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { getPageStartSource, getSharedProperties } from 'Utils/Analytics/SharedProperties';
import { getAnalyticsUserId } from 'Utils/Analytics/SegmentShared';
import Cookies from 'universal-cookie';
import { trackPageName } from 'State/Tracking';
import config from 'Config/analytics.json';
import { getSessionCampaignTraits } from './sessionCampaignTraits';
import { getSegmentAmplitudeString, resetAmplitudeUser } from './Amplitude';
import { getDefaultConfig, getState } from 'Utils/Store';
import { isFreeTrial } from 'Utils/Plans';
import { isFirstResponderPlan, isMilitaryPlan } from 'Utils/SheerIdHelpers';
import { isVeteransAdvPlan } from 'Utils/VeteransAdvHelpers';
import { getLastAnonymousProfileId } from '@foxcorp/cpe-commerce-web';

export const TRACKING_ENUMS = (config && config.enums) || {};
const ANALYTICS_PROPS = (config && config.properties) || {};
const SEGMENT_FORMS = (config && config.segment && config.segment.forms) || {};
export const SEGMENT_EVENTS = (config && config.segment && config.segment.events) || {};
const SEGMENT_ERRORS = (config && config.segment && config.segment.errors) || {};
const SEGMENT_PAGES = (config && config.segment && config.segment.pages) || {};
const cookies = new Cookies();
const CAMPAIGN_TRACKING_CODE_KEY = 'campaign_tracking_code';
const INT_CAMPAIGN_TRACKING_CODE_KEY = 'campaign_int_tracking_code';

const getLastKnownProfileId = () => {
  const { lastKnownProfileIdCookieName } = getDefaultConfig();
  return cookies.get(lastKnownProfileIdCookieName);
};

export const getPageSourceMLP = () => {
  const { purchaseFunnelPageSourceCookieName } = getDefaultConfig();
  return cookies.get(purchaseFunnelPageSourceCookieName);
};

const getPathname = () => (window && window.location && window.location.pathname) || '';
const getHref = () => (window && window.location && window.location.href) || '';

const getEntitlementTrait = () => {
  const { hasEntitlements, entitlements } = getState('user');
  const entitlementList = entitlements.reduce((acc, item) => {
    return [...acc, item.appId, item.serviceID];
  }, []);
  const networkEntitlementList = hasEntitlements ? entitlementList.join(',') : '';
  return { network_entitlement_list: networkEntitlementList };
};

export const getPageProfileType = (userType, plans, appliedCoupon) => {
  const { selectedPlan } = plans;

  if (selectedPlan) {
    // check for military plans first because they could also be free trials, but the military type has priority
    if (isMilitaryPlan(selectedPlan.packageID) || isVeteransAdvPlan(selectedPlan.packageID)) return 'Military';
    if (isFirstResponderPlan(selectedPlan.packageID)) return 'First Responders';
    if (isFreeTrial(selectedPlan)) return 'Trialist';
    if (appliedCoupon || selectedPlan.promotionID) return 'DTP';
  }

  return userType === 'email' || null ? 'general' : userType;
};

const getUserProperty = (eventName) => {
  if (!eventName) return {};
  const {
    user: { userType },
    appConfig: { routes },
    plans,
    promoCode: { couponCode, isValid },
  } = getState();

  const pathName = getPathname();
  const isGiftingHome = pathName.includes(routes && routes.giftingHome);
  const profileType = getPageProfileType(userType, plans, !!(couponCode && isValid));

  return { page_profile_type: isGiftingHome ? undefined : profileType };
};

const getAmplitudeExperimentData = () => {
  const { enableSegmentTestname } = getDefaultConfig();

  const experiments = enableSegmentTestname ? getSegmentAmplitudeString() : undefined;
  return { ...(experiments && { page_test_name: experiments }) };
};

const getPageProperties = () => {
  const { referrer = '', title = '' } = (window && window.document) || {};
  const { pathname = '', search = '', origin = '' } = (window && window.location) || {};
  return {
    path: pathname,
    url: origin + pathname,
    referrer,
    search,
    title,
  };
};

const getProperties = (properties, eventName) => {
  const { entitlementId, appVersion, buildNumber } = getDefaultConfig();
  const { setPageName, page_name, ...props } = properties || {};
  const pathName = getPathname();
  const urlHref = getHref();
  const appProperties = ANALYTICS_PROPS[entitlementId] || {};
  const defaultProperties = { ...ANALYTICS_PROPS.common, ...appProperties };
  const campaignTraits = omit(getSessionCampaignTraits(), CAMPAIGN_TRACKING_CODE_KEY);

  return {
    ...defaultProperties,
    app_version: appVersion,
    app_build: buildNumber,
    url: urlHref.split(/[?#]/)[0],
    path: pathName,
    ...campaignTraits,
    ...getUserProperty(eventName),
    ...getAmplitudeExperimentData(),
    ...getSharedProperties(eventName, properties),
    ...props,
  };
};

const getTraits = (userId = null) => {
  const id = userId || getAnalyticsUserId();
  const {
    user: { profileId, mvpdId },
    tracking: { purchaseSource },
  } = getState();

  const traitByName = pick(getSessionCampaignTraits(), CAMPAIGN_TRACKING_CODE_KEY, INT_CAMPAIGN_TRACKING_CODE_KEY);
  const traits = {
    purchase_source: purchaseSource,
    lastAnonymousProfileId: getLastAnonymousProfileId(),
    isSignedIntoProfile: id != null,
    mvpd: mvpdId || '',
    dcg_profile_id: profileId || '',
    appsflyer_id: '',
    ...getEntitlementTrait(),
    [CAMPAIGN_TRACKING_CODE_KEY]: traitByName[CAMPAIGN_TRACKING_CODE_KEY] || '',
    [INT_CAMPAIGN_TRACKING_CODE_KEY]: traitByName[INT_CAMPAIGN_TRACKING_CODE_KEY] || '',
  };

  const lastKnownProfileId = getLastKnownProfileId();
  if (lastKnownProfileId != null) {
    traits.lastKnownProfileId = lastKnownProfileId;
  }

  return traits;
};

const getFormName = (name) => SEGMENT_FORMS[name] || null;

const isAccountError = (event) =>
  event === 'editPaymentForm' || event === 'updateAccountPasswordForm' || event === 'updateAccountForm';

const getSegmentFormTrackName = (formId, event) => {
  const formName = getFormName(formId);
  if (!formName) return null;

  const eventName = SEGMENT_EVENTS[event];
  if (!eventName) return null;

  return `${formName} ${eventName}`;
};

const getSegmentError = (error) => {
  if (!error) return {};

  let errorType = 'Other';
  let errorDetails = 'Unknown Error';

  if (typeof error === 'string') {
    errorDetails = error;
  } else {
    errorDetails = SEGMENT_ERRORS[error.details] || SEGMENT_ERRORS[error.message] || error.details || error.message;
    const errorCode = parseInt(error.status, 10);
    if (errorCode > 600) {
      if (errorCode === 1000) {
        errorType = 'Invalid Form Input';
      }
    } else if (errorCode != null) {
      errorType = 'Server-side';
    }
  }

  return {
    page_error_type: errorType,
    page_error_description: errorDetails,
  };
};

const track = (name, properties, eventType = 'Event') => {
  console.debug(`Segment [${eventType}]: ${name}`);
  const trackProperties = getProperties(properties, name);
  window.analytics.ready(() => {
    // Cleanup undefined and null values
    Object.keys(trackProperties).forEach((key) => trackProperties[key] === null && delete trackProperties[key]);
    const options = { traits: getTraits(), page: getPageProperties() };
    window.analytics.track(name, trackProperties, options);
  });
};

export const identify = (userId = null, anonymousId) => {
  console.debug(`Segment [Identify]: ${userId}`);
  window.analytics.ready(() => {
    const traits = getTraits();
    const page = getPageProperties();
    window.analytics.identify(userId, traits, { traits, page, anonymousId });
    //calling identify resets the segment anonymous id, so want to reset the amplitude user as well
    //https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/
    resetAmplitudeUser();

    const dataLayerTraits = {
      lastAnonymousProfileId: traits.lastAnonymousProfileId,
      dcg_profile_id: traits.dcg_profile_id,
    };
    if (traits.lastKnownProfileId != null) {
      dataLayerTraits.lastKnownProfileId = traits.lastKnownProfileId;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerTraits);
  });
};

const trackPageData = async (name, pathName, eventName) => {
  const pageProperties = SEGMENT_PAGES[eventName] || {};
  const trackPageProperties = getProperties(pageProperties);
  const options = { page: getPageProperties() };

  try {
    const traits = await getTraits();
    if (traits) options.traits = traits;
  } catch (error) {
    console.error('Error from getTraits() in trackPageData:', error);
  }

  window.analytics.page(null, name, trackPageProperties, options);
  if (!isEmpty(pageProperties)) {
    trackPageName(pageProperties); // persist the previous page call properties.
  }
};

export const trackPage = (name, pathName, eventName) => {
  if (!name) return;
  console.debug(`Segment [Page]: ${name} Page Viewed (${pathName})`);
  window.analytics.ready(() => {
    trackPageData(name, pathName, eventName);
  });
};

export const initPageCall = (name, pathName, eventName) => {
  if (!name) return;
  console.debug(`Segment [Page]: ${name} Page Viewed (${pathName})`);
  trackPageData(name, pathName, eventName);
};

export const trackForm = (event, name, customProperties = {}) => {
  const trackName = getSegmentFormTrackName(name, event);
  if (!trackName) return;
  try {
    track(trackName, customProperties, 'Form');
  } catch (e) {
    console.error('Occurred an error while sending tracking data', e);
  }
};

export const trackFormError = (formId, error, customProperties = {}) => {
  if (!error) return;
  const trackName = getSegmentFormTrackName(formId, TRACKING_ENUMS.EVENT_ERROR);
  if (!trackName) return;

  const properties = { ...getSegmentError(error), ...customProperties, ...getPageStartSource() };

  try {
    track(trackName, properties, 'Form_Error');
    // Keep legacy account error events with new leapfrog error account event
    if (isAccountError(formId)) track(SEGMENT_EVENTS.updateAccountError, properties, 'Form_Error');
  } catch (e) {
    console.error('Occurred an error while sending tracking data', e);
  }
};

export const trackEvent = (event, customProperties = {}) => {
  const eventName = SEGMENT_EVENTS[event];
  if (eventName) {
    try {
      track(eventName, customProperties);
    } catch (e) {
      console.error('Occurred an error while sending tracking data', e);
    }
  }
};
